import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle2, Coffee, HelpCircle, Sparkles, Zap } from "lucide-react";

// KLA-029: light rotating lines while Klair composes, in her voice.
const THINK_LINES = [
  "hmm, that one made me think",
  "putting your last answer in its place",
  "picking something actually worth your time",
  "connecting this to what you said earlier",
  "good answers deserve a good next question",
];

// KLA-034: small warm facts for the break card. Light, no pressure.
const BREAK_FACTS = [
  "chai and tea both come from the same old Chinese word, they just travelled different roads",
  "a short break usually makes the next answer more honest, not less",
  "your answers are saved exactly where you left them, no rush at all",
  "more than half of India is younger than 30, you are in very big company",
  "your brain keeps quietly working on a question even while you sip your chai",
];
import client, { errText, streamSSE } from "../lib/api";
import { guardCrisis } from "../lib/safety";
import iconMark from "../assets/brand/klareet-icon-white.png";
import { LAYER_ORDER } from "../components/RadarChart";

const LIKERT_LABELS = ["not really", "a little", "sort of", "mostly", "very much"];

// KLA-053: on a touch phone the soft keyboard's Return is how people
// add a line break, so Enter must NOT submit there; the Send button
// does that. Enter-to-send stays a desktop convenience only.
function isCoarsePointer() {
  return typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(pointer: coarse)").matches;
}

// Human typing cadence. A small base delay per character, a tiny beat at
// the end of a sentence and a shorter one at a comma, plus a little jitter
// so the rhythm never feels mechanical. While words are still arriving we
// speed up if a lot is buffered ahead, so Klair never lags far behind her
// own thought; once her line is complete we let it type at the warm pace.
function typingDelay(ch, buffered) {
  let d = 9 + Math.random() * 15; // ~9-24ms base
  if (ch === "." || ch === "!" || ch === "?") d += 170 + Math.random() * 150;
  else if (ch === "," || ch === ";" || ch === ":") d += 70 + Math.random() * 70;
  else if (ch === "\n") d += 180;
  else if (ch === " ") d += 5;
  if (buffered > 160) d *= 0.3;
  else if (buffered > 80) d *= 0.55;
  return d;
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Map an item construct name to one of the 7 spectrum layer keys.
function constructToLayer(construct) {
  const key = (construct || "").toLowerCase();
  if (!key) return null;
  const rules = [
    { keys: ["extra", "conscien", "agreeable", "openness", "neurotic", "personality"], layer: "personality" },
    { keys: ["cognitive", "reason", "verbal", "numeric", "learn_speed", "processing", "logical", "spatial"], layer: "cognitive" },
    { keys: ["emotion", "eq", "self_aware", "empathy", "regulation", "resilience", "stability"], layer: "emotional" },
    { keys: ["interest", "curiosity", "domain", "affinity", "realistic", "investigative", "artistic", "enterprising", "conventional", "social"], layer: "interests" },
    { keys: ["value", "purpose", "ethic", "meaning", "principle", "integrity"], layer: "values" },
    { keys: ["compet", "skill", "capability", "craft", "execution", "leadership", "decision"], layer: "competencies" },
    { keys: ["ready", "readiness", "posture", "commit", "focus", "adapt", "risk", "motivator"], layer: "readiness" },
  ];
  for (const r of rules) {
    if (r.keys.some((k) => key.includes(k))) return r.layer;
  }
  let h = 0; for (const c of key) h = ((h * 31) + c.charCodeAt(0)) & 0xffff;
  return LAYER_ORDER[h % LAYER_ORDER.length].key;
}

/**
 * Klareet assessment.
 * Two clear movements. First a real conversation where Klair gets to know
 * you (chat). Then, past the gate, a focused deep dive: one item at a time,
 * with Klair's bridge in her voice and the authored question verbatim
 * underneath. Scoring, selection, and the care rail run server side.
 *
 * Klair's words stream in live, token by token, over Server-Sent Events so
 * her longer thinking never reads as a freeze. If the stream ever fails, the
 * turn falls back cleanly to the plain request/response endpoint. When she
 * has to answer extra quickly, a very quiet cue appears near her name.
 */
export default function ConversationScreen({ product }) {
  const nav = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [current, setCurrent] = useState(null);
  const [messages, setMessages] = useState([]); // {who, text, animated?}
  const [text, setText] = useState("");
  const [ownWords, setOwnWords] = useState("");
  // KLA-004: on an MCQ card a tap selects; a note can ride along; Next submits both.
  const [tapChoice, setTapChoice] = useState(null); // {value, label}
  const [tapNote, setTapNote] = useState("");
  // KLA-031: a choice card (gate, hostile, resume, care) works exactly
  // once; after one click its palette stays dead even when busy clears.
  const [usedCardKey, setUsedCardKey] = useState("");
  // KLA-021: idea starters open on tap, or by themselves after 15 quiet
  // seconds; typing cancels the auto-open.
  const [ideasOpen, setIdeasOpen] = useState(false);
  const ideasTimerRef = useRef(null);
  // KLA-029/034: rotating think lines and break facts.
  const [thinkIdx, setThinkIdx] = useState(0);
  const [breakIdx, setBreakIdx] = useState(0);
  const [ctxPick, setCtxPick] = useState("");
  const [showOwnWords, setShowOwnWords] = useState(false);
  const [helper, setHelper] = useState("");
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");
  const [layersSeen, setLayersSeen] = useState(new Set());
  const [streamText, setStreamText] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [snappy, setSnappy] = useState(false);
  const scrollRef = useRef(null);
  const endRef = useRef(null);
  const bootedRef = useRef(false);
  const targetRef = useRef("");        // full text received so far
  const streamDoneRef = useRef(false); // no more tokens are coming
  const typingTimerRef = useRef(null); // the typewriter's scheduled tick
  const base = product === "grow" ? "/grow" : "";

  const scroll = () => {
    // KLA-026 + KLA-053: the WINDOW is the real scroller. Follow new
    // content only when the reader is already near the bottom, and on
    // phones use the VISUAL viewport (the keyboard shrinks it) plus an
    // end anchor with scroll margin, so the view lands just under the
    // last bubble instead of overshooting behind the composer bar.
    setTimeout(() => {
      const el = document.scrollingElement || document.documentElement;
      const vh = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      const nearBottom = vh + window.scrollY >= el.scrollHeight - 380;
      if (nearBottom && endRef.current) {
        endRef.current.scrollIntoView({ block: "end", behavior: "smooth" });
      }
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 40);
  };

  const clearTypingTimer = () => {
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
    }
  };

  // The typewriter. Reveals targetRef into streamText one character at a
  // time at a warm, human pace with tiny pauses between sentences. Resolves
  // once the full line is revealed and the stream has finished.
  const revealText = () => new Promise((resolve) => {
    let revealed = 0;
    const reduced = prefersReducedMotion();
    const tick = () => {
      const target = targetRef.current;
      if (revealed >= target.length) {
        if (streamDoneRef.current) { resolve(); return; }
        // Caught up but Klair is still thinking; hold, caret blinking.
        typingTimerRef.current = setTimeout(tick, 40);
        return;
      }
      if (reduced) {
        revealed = target.length;
        setStreamText(target);
        typingTimerRef.current = setTimeout(tick, 0);
        return;
      }
      revealed += 1;
      const ch = target[revealed - 1];
      setStreamText(target.slice(0, revealed));
      // Only chase incoming tokens while more are still on the way; once the
      // line is complete, type it out at the full warm rhythm.
      const buffered = streamDoneRef.current ? 0 : target.length - revealed;
      typingTimerRef.current = setTimeout(tick, typingDelay(ch, buffered));
    };
    tick();
  });

  // Run one streamed turn. Klair's shell arrives first, then her words are
  // buffered and typed in at a human pace, then the authoritative turn.
  // Returns { ok, turn }; ok is false on any failure so the caller can fall
  // back to the plain endpoint. On success, streaming state is left for
  // finalizeTurn to settle atomically (no flicker between the typed line and
  // the settled bubble).
  const runStream = async (path, body) => {
    clearTypingTimer();
    targetRef.current = "";
    streamDoneRef.current = false;
    setStreamText("");
    setSnappy(false);
    setStreaming(true);
    let finalTurn = null;
    let failed = false;
    const typingDone = revealText();
    try {
      await streamSSE(path, body, {
        onShell: (turn) => {
          setCurrent(turn);
          setHelper("");
          setShowOwnWords(false);
          setOwnWords("");
        },
        onToken: (t) => { targetRef.current += t; },
        onDone: (turn) => {
          finalTurn = turn;
          if (turn && typeof turn.message === "string" && turn.message) {
            targetRef.current = turn.message;
          }
        },
        onError: () => { failed = true; },
      });
    } catch (e) {
      failed = true;
    }
    streamDoneRef.current = true;
    if (failed || !finalTurn) {
      clearTypingTimer();
      setStreaming(false);
      return { ok: false };
    }
    await typingDone; // let Klair finish typing her line before it settles
    return { ok: true, turn: finalTurn };
  };

  // Settle a finished turn: record Klair's line, surface the snappy cue if it
  // fired, then swap in the next card (or hold on an explain). Clears the
  // streaming state in the same render so the typed line hands off to the
  // settled bubble seamlessly.
  const finalizeTurn = (nxt) => {
    clearTypingTimer();
    if (nxt?.message) setMessages((m) => [...m, { who: "klair", text: nxt.message }]);
    setSavedAt(new Date());
    setSnappy(!!nxt?.fast);
    setStreamText("");
    setStreaming(false);
    if (nxt?.stage === "explain") {
      setHelper(nxt.helper || "");
      // Issue 7: clear the typed input on an explain turn too, so the words
      // do not linger in the box on the re-posed item.
      setShowOwnWords(false);
      setOwnWords("");
      return;
    }
    setHelper("");
    setShowOwnWords(false);
    setOwnWords("");
    setCurrent(nxt);
  };

  useEffect(() => {
    // StrictMode double-invokes effects in dev, and a second boot would
    // race a stream against the first. Run exactly once.
    if (bootedRef.current) return;
    bootedRef.current = true;
    const boot = async () => {
      try {
        // Resume an in progress conversation if one exists (nothing lost
        // on a tab close), otherwise start fresh.
        const { data: cur } = await client.get(`/demo/session/current?product=${product}`);
        if (cur && cur.resumed) {
          setSessionId(cur.session_id);
          setCurrent(cur.turn);
          const seed = [];
          if (cur.welcome_back) seed.push({ who: "klair", text: cur.welcome_back, animated: true });
          if (cur.turn?.message) seed.push({ who: "klair", text: cur.turn.message, animated: true });
          setMessages(seed);
          return;
        }
      } catch (e) { /* fall through to fresh start */ }

      const body = {
        product,
        intake: JSON.parse(localStorage.getItem("klareet.intake") || "{}"),
      };
      try {
        const { ok, turn } = await runStream("/demo/session/start/stream", body);
        if (ok && turn) {
          setSessionId(turn.session_id);
          finalizeTurn(turn);
          return;
        }
        // Stream failed: plain start.
        const { data } = await client.post("/demo/session/start", body);
        setSessionId(data.session_id);
        finalizeTurn(data.turn);
      } catch (e) {
        setErr(errText(e, "We could not start the conversation."));
      }
    };
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(scroll, [messages, streamText, current]);

  // KLA-053: when the mobile keyboard opens or closes, the layout does
  // not change, so the scroll effect above never re-fires and the input
  // can end up behind the keyboard. Follow the visual viewport instead.
  useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return undefined;
    const onResize = () => scroll();
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // KLA-021: arm the 15-second idea reveal per card, once the question
  // itself has landed; a new card always starts closed.
  useEffect(() => {
    setIdeasOpen(false);
  }, [current]);
  useEffect(() => {
    if (ideasTimerRef.current) { clearTimeout(ideasTimerRef.current); ideasTimerRef.current = null; }
    const hasIdeas = Array.isArray(current?.suggested_replies) && current.suggested_replies.length > 0;
    if (!hasIdeas || ideasOpen || streaming) return undefined;
    ideasTimerRef.current = setTimeout(() => setIdeasOpen(true), 15000);
    return () => { if (ideasTimerRef.current) { clearTimeout(ideasTimerRef.current); ideasTimerRef.current = null; } };
  }, [current, streaming, ideasOpen]);
  useEffect(() => {
    // Typing means they are answering; the auto-open would only distract.
    if (text.trim() && ideasTimerRef.current) { clearTimeout(ideasTimerRef.current); ideasTimerRef.current = null; }
  }, [text]);

  // KLA-029: rotate the thinking line while Klair composes.
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(() => setThinkIdx((i) => i + 1), 2600);
    return () => clearInterval(t);
  }, [busy]);

  // KLA-034: rotate the break fact while the break card is up.
  useEffect(() => {
    if (current?.answer_kind !== "resume_choice") return undefined;
    const t = setInterval(() => setBreakIdx((i) => i + 1), 8000);
    return () => clearInterval(t);
  }, [current]);

  // Stop the typewriter if the screen unmounts mid-line.
  useEffect(() => () => {
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
  }, []);

  // A fresh card clears any MCQ selection and note (KLA-004), and E4: it
  // clears the used-card lock so a new choice card, including a second care
  // or distress card, always renders with live buttons.
  useEffect(() => {
    setTapChoice(null);
    setTapNote("");
    setUsedCardKey("");
  }, [current]);

  // Track which layers have received a turn, for the live rail.
  useEffect(() => {
    if (!current) return;
    const layer = constructToLayer(current.construct);
    if (!layer) return;
    setLayersSeen((prev) => {
      if (prev.has(layer)) return prev;
      const next = new Set(prev);
      next.add(layer);
      return next;
    });
  }, [current]);

  const submit = async (answerValue, answerText, skip, note) => {
    if (busy || !current) return;
    if (answerText && guardCrisis(answerText)) return;
    if (note && guardCrisis(note)) return;
    setBusy(true);
    setErr("");
    const baseShown =
      answerText || (typeof answerValue === "number" ? LIKERT_LABELS[answerValue - 1] : (skip ? "skipped" : ""));
    const shownText = note ? (baseShown ? `${baseShown}. ${note}` : note) : baseShown;
    if (shownText) setMessages((m) => [...m, { who: "you", text: shownText }]);
    setText("");
    const payload = {
      session_id: sessionId,
      answer_value: typeof answerValue === "number" ? answerValue : null,
      answer_text: answerText || null,
      note: note || null,
      // Issue 7: tell the server which item this answer belongs to, so it
      // binds to the item on screen (never a re-pick) and a retried submit
      // cannot land the answer on the next question.
      item_id: current?.item_id || null,
    };
    try {
      const { ok, turn } = await runStream("/demo/session/turn/stream", payload);
      let nxt = turn;
      if (!ok) {
        const { data } = await client.post("/demo/session/turn", payload);
        nxt = data.turn;
      }
      finalizeTurn(nxt);
      if (nxt?.done) {
        setTimeout(async () => {
          try {
            const { data: report } = await client.post("/demo/synthesise", { session_id: sessionId });
            localStorage.setItem("klareet.report", JSON.stringify(report));
            nav(`${base}/building`);
          } catch (e) {
            setErr("We could not build your read. Please try again.");
          }
        }, 350);
      }
    } catch (e) {
      setErr(errText(e, "That did not go through."));
    } finally {
      setBusy(false);
    }
  };

  const askExplain = async () => {
    if (busy || !sessionId) return;
    try {
      const { data } = await client.post("/demo/session/explain", { session_id: sessionId });
      setHelper(data.helper || "");
    } catch (e) {
      setHelper("No worries. Just answer however feels closest to the truth.");
    }
  };

  const stage = current?.stage;
  const ui = current?.ui;
  const isItem = ui === "item" && stage === "klair-item";
  const answerKind = current?.answer_kind;
  const isNarrative = isItem && answerKind === "text";
  const isLikert = isItem && answerKind === "likert5";
  const isTap = isItem && answerKind === "tap";

  // Chat-phase controls.
  const isChatText = !isItem && answerKind === "text";
  const isContext = !isItem && answerKind === "context";
  const isGate = answerKind === "gate_choice";
  // KLA-021/022: nothing below the ack reveals until Klair has finished
  // streaming her response to the previous answer.
  const revealed = !streaming;
  const isCareChoice = answerKind === "care_choice";
  const isHostile = answerKind === "hostile_choice";
  const isResume = answerKind === "resume_choice";

  const activeLayer = current ? constructToLayer(current.construct) : null;
  const isCareRail = ["care-rail", "care-rail-continue", "care-rail-stop"].includes(stage);

  // Klair's bridge inside the deep-dive card: live while streaming, settled
  // once the turn lands.
  const liveBridge = streaming ? streamText : current?.bridge;

  // Progress: soft, finite, honest.
  const total = current?.total_turns || 18;
  const idx = current?.turn_index || 0;
  const narrTotal = 6;
  const adaptiveTotal = Math.max(1, total - narrTotal);
  const adaptiveIdx = Math.max(0, idx - narrTotal);
  const percent = current ? Math.min(100, Math.round((idx / total) * 100)) : 0;
  const progressLabel = !current
    ? "starting"
    : isCareRail
    ? "we are pausing"
    : isResume
    ? "saved for you"
    : isGate
    ? "one quick check in"
    : isItem
    ? `${Math.min(adaptiveIdx + 1, adaptiveTotal)} of about ${adaptiveTotal}`
    : "getting to know you";

  const savedLine = savedAt ? "Saved just now" : "Saved as you go";

  return (
    <div className="k-shell k-conv relative overflow-hidden">
      <div className="k-orb k-orb-blue" style={{ top: "-8rem", right: "-6rem", width: "20rem", height: "20rem" }} />
      <div className="k-orb k-orb-teal" style={{ bottom: "8rem", left: "-8rem", width: "20rem", height: "20rem" }} />
      <div className="relative max-w-3xl mx-auto px-4 pt-8 pb-40">
        {/* Klair identity bar */}
        <div className="flex items-center gap-3 mb-6" data-testid="klair-identity">
          <div className="relative w-11 h-11 rounded-[14px] flex items-center justify-center" style={{ background: "#1A202C" }}>
            <span className="k-ring-pulse" aria-hidden="true"></span>
            <img src={iconMark} alt="" aria-hidden="true" className="w-6 h-6" />
          </div>
          <div className="leading-tight">
            <div className="font-semibold flex items-center gap-1.5">
              Klair
              {/* Snappy Mode: a very quiet cue. When Klair had to answer
                  extra quickly, a faint dot appears. It names no model and
                  is easy to miss on purpose. */}
              {snappy && (
                <span
                  className="k-snappy-cue inline-flex items-center"
                  title="Klair answered extra quickly just now"
                  data-testid="snappy-hint"
                  aria-label="Klair answered quickly"
                >
                  <Zap size={11} strokeWidth={2.25} />
                </span>
              )}
            </div>
            <div className="k-quiet" style={{ marginTop: -1 }}>your companion</div>
          </div>
          <div className="ml-auto text-right">
            <div className="k-quiet klareet-mono" data-testid="turn-counter">{progressLabel}</div>
            <div className="text-xs" style={{ color: "hsl(var(--klareet-muted-2))" }} data-testid="auto-save">{savedLine}</div>
          </div>
        </div>

        <div className="k-progress-gradient mb-2" data-testid="conversation-progress">
          <i style={{ width: `${percent}%` }} />
        </div>

        {/* Live 7-layer rail. */}
        <div className="flex items-center justify-between mb-6" data-testid="layer-rail">
          <div className="k-layer-rail">
            {LAYER_ORDER.map((L) => {
              const seen = layersSeen.has(L.key);
              const active = activeLayer === L.key;
              return (
                <span
                  key={L.key}
                  className={`k-layer-dot ${active || seen ? "on" : ""}`}
                  style={{
                    background: (active || seen) ? `hsl(var(--k-${L.hue}))` : "hsl(var(--klareet-line))",
                    boxShadow: active ? `0 0 0 4px hsl(var(--k-${L.hue}) / 0.22)` : "none",
                  }}
                  title={L.label}
                  data-testid={`rail-dot-${L.key}${active ? "-active" : ""}${seen && !active ? "-seen" : ""}`}
                />
              );
            })}
          </div>
          <div className="k-quiet !text-xs" data-testid="rail-count">{layersSeen.size} of 7 layers touched</div>
        </div>

        {/* DEEP DIVE: focused wizard card, one item at a time. */}
        {isItem ? (
          <div className="k-card p-6 k-fade-in" data-testid="item-card">
            <div className="flex items-center gap-2 mb-4">
              <span className="k-chip" data-testid="type-chip">{current.type_chip || "How much is this you"}</span>
              <button
                onClick={askExplain}
                disabled={busy}
                className="ml-auto inline-flex items-center gap-1 text-sm k-quiet hover:opacity-80"
                data-testid="explain-this"
              >
                <HelpCircle size={15} /> explain this
              </button>
            </div>

            {/* Klair's bridge, in her voice, streaming in live. */}
            {liveBridge && !isNarrative && (
              <p className="mb-4" data-testid="item-bridge" style={{ color: "hsl(var(--klareet-muted-1))" }}>
                {liveBridge}
                {streaming && <span className="k-caret" aria-hidden="true" />}
              </p>
            )}

            {/* KLA-022: the ack (Klair's bridge) streams FIRST; the
                question and its controls reveal only once she has
                finished responding to the previous answer. */}
            {revealed && current.stem ? (
              <p className="klareet-serif text-xl leading-snug mb-5 k-pop-in" data-testid="item-stem">{current.stem}</p>
            ) : (
              revealed && isNarrative && <p className="klareet-serif text-xl leading-snug mb-5 k-pop-in" data-testid="item-stem">{liveBridge}</p>
            )}

            {revealed && helper && (
              <div className="k-note mb-5" data-testid="item-helper">{helper}</div>
            )}

            {/* Controls matched to the kind. KLA-021: options never
                appear before the question has landed. */}
            {revealed && isLikert && (
              <div className="flex gap-2" data-testid="likert-palette">
                {LIKERT_LABELS.map((label, i) => (
                  <button
                    key={i}
                    onClick={() => submit(i + 1, null, false)}
                    disabled={busy}
                    className="k-opt k-likert k-pop-in"
                    style={{ animationDelay: `${i * 50}ms` }}
                    data-testid={`likert-${i + 1}`}
                  >
                    <span className="k-likert-dots" aria-hidden="true">
                      {Array.from({ length: i + 1 }).map((_, d) => <i key={d} />)}
                    </span>
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            )}
            {revealed && isTap && (
              <div className="flex flex-col gap-2.5" data-testid="tap-palette">
                {(current.quick_replies || []).map((opt, i) => {
                  const val = Math.min(i + 1, 5);
                  const on = tapChoice?.value === val && tapChoice?.label === opt;
                  return (
                    <button
                      key={i}
                      onClick={() => setTapChoice(on ? null : { value: val, label: opt })}
                      disabled={busy}
                      aria-pressed={on}
                      className={`k-opt k-pop-in ${on ? "k-opt-on" : ""}`}
                      style={{ animationDelay: `${i * 60}ms` }}
                      data-testid={`tap-option-${i + 1}`}
                    >
                      <span className="k-opt-badge">{String.fromCharCode(65 + i)}</span>
                      <span>{opt}</span>
                      <CheckCircle2 size={20} className="k-opt-check" aria-hidden="true" />
                    </button>
                  );
                })}
                {/* KLA-030: an option question takes an option. The note
                    (KLA-004) rides along with the pick where the card
                    allows it; puzzles (allow_note false) get no box. */}
                {current?.allow_note !== false && (
                  <textarea
                    data-testid="tap-note-input"
                    aria-label="Add a note to your choice, optional"
                    className="k-input resize-none mt-1"
                    rows={2}
                    placeholder="add a note to your pick (optional)"
                    value={tapNote}
                    onChange={(e) => setTapNote(e.target.value)}
                    disabled={busy}
                  />
                )}
                <div className="flex items-center justify-between gap-3 mt-1">
                  <span className="k-quiet !text-xs" data-testid="tap-next-hint">
                    {tapChoice ? "picked: " + tapChoice.label : "tap the option that fits to continue"}
                  </span>
                  <button
                    onClick={() => {
                      if (!tapChoice) return;
                      const note = current?.allow_note !== false ? tapNote.trim() : "";
                      submit(tapChoice.value, tapChoice.label, false, note || null);
                    }}
                    disabled={busy || !tapChoice}
                    className="k-btn k-btn-gradient"
                    data-testid="tap-next"
                  >Next <ArrowRight size={16} /></button>
                </div>
              </div>
            )}
            {revealed && isNarrative && (
              <div>
                {/* KLA-018 + KLA-021: idea starters stay behind a small
                    "need ideas?" tap and self-open after 15 quiet
                    seconds, so they help the stuck without crowding the
                    question. Typing wins; typing also cancels the timer. */}
                {Array.isArray(current?.suggested_replies) && current.suggested_replies.length > 0 && !ideasOpen && (
                  <button type="button" onClick={() => setIdeasOpen(true)} className="k-suggest mb-2" data-testid="narrative-need-ideas">
                    <Sparkles size={13} aria-hidden="true" /> need ideas?
                  </button>
                )}
                {Array.isArray(current?.suggested_replies) && current.suggested_replies.length > 0 && ideasOpen && (
                  <div className="mb-2" data-testid="narrative-suggested">
                    <div className="flex flex-wrap gap-2">
                      {current.suggested_replies.map((s, i) => (
                        <button key={i} type="button" onClick={() => setText(s)} disabled={busy} className="k-suggest k-pop-in" style={{ animationDelay: `${i * 55}ms` }} data-testid={`narrative-suggested-${i + 1}`}><Sparkles size={13} aria-hidden="true" /> {s}</button>
                      ))}
                    </div>
                    <p className="k-quiet !text-xs mt-1.5">tap one to start, or say it your own way</p>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea
                    data-testid="narrative-input"
                    aria-label="Your answer to Klair"
                    className="k-input resize-none klareet-serif"
                    rows={2}
                    placeholder="say it how you'd say it"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !isCoarsePointer()) { e.preventDefault(); if (text.trim()) submit(null, text.trim(), false); } }}
                  />
                  <button onClick={() => text.trim() && submit(null, text.trim(), false)} disabled={busy || !text.trim()} className="k-btn k-btn-primary" data-testid="narrative-send" aria-label="send"><ArrowRight size={16} /></button>
                </div>
              </div>
            )}

            {/* Add your own words (optional context on scored items). */}
            {current.allow_own_words && (
              <div className="mt-5 pt-4 border-t" style={{ borderColor: "hsl(var(--klareet-line-soft))" }}>
                {!showOwnWords ? (
                  <button onClick={() => setShowOwnWords(true)} className="inline-flex items-center gap-1 text-sm k-quiet hover:opacity-80" data-testid="add-own-words-toggle"><Sparkles size={14} /> add your own words</button>
                ) : (
                  <div className="flex items-end gap-2">
                    <textarea data-testid="own-words-input" aria-label="Say this one in your own words" className="k-input resize-none" rows={2} placeholder="anything you want me to know about this one" value={ownWords} onChange={(e) => setOwnWords(e.target.value)} />
                    <button onClick={() => ownWords.trim() && submit(null, ownWords.trim(), false)} disabled={busy || !ownWords.trim()} className="k-btn k-btn-ghost" data-testid="own-words-send" aria-label="send words"><ArrowRight size={16} /></button>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-center justify-between mt-5">
              {isLikert ? (
                <button onClick={() => submit(3, null, true)} disabled={busy} className="k-btn k-btn-ghost text-sm !py-1.5 !px-3" data-testid="skip-turn">Skip</button>
              ) : <span />}
              {busy ? (
                <span className="k-quiet text-xs inline-flex items-center gap-1.5 k-fade-in" data-testid="item-thinking">
                  <span className="k-think-dot" /> {streaming && streamText ? "Klair is talking" : THINK_LINES[thinkIdx % THINK_LINES.length]}
                </span>
              ) : (
                <span className="k-quiet text-xs" data-testid="item-saved">{savedAt ? "Saved just now, leave and come back anytime" : "Auto-saves as you go"}</span>
              )}
            </div>
          </div>
        ) : (
          /* CONVERSATION: warm chat stream. */
          <>
            <p className="k-quiet mb-6">I'm keeping track of everything you tell me, so you never repeat yourself.</p>
            {/* KLA-058: a screen reader hears the whole product, which is
                a conversation. The per-token typewriter is aria-hidden
                (it would machine-gun the reader); the COMPLETE Klair line
                is announced once, politely, from this off-screen region. */}
            <div className="sr-only" role="status" aria-live="polite" aria-atomic="true" data-testid="klair-live-region">
              {!streaming ? (messages.filter((m) => m.who === "klair").slice(-1)[0]?.text || "") : ""}
            </div>
            <div ref={scrollRef} className="space-y-4 min-h-[36vh]">
              {messages.map((m, i) => (
                <div key={i} className={m.who === "you" ? "flex justify-end" : "flex"}>
                  <div className={`k-bubble ${m.who === "you" ? "k-bubble-you" : "k-bubble-klair"} ${m.animated ? "k-fade-in" : ""}`} data-testid={m.who === "klair" ? "klair-bubble" : "you-bubble"}>{m.text}</div>
                </div>
              ))}
              {/* Live streaming bubble: Klair's words arrive token by token. */}
              {streaming && !isItem && (
                <div className="flex" aria-hidden="true">
                  <div className="k-bubble k-bubble-klair k-fade-in" data-testid="klair-streaming-bubble">
                    {streamText ? (
                      <>{streamText}<span className="k-caret" aria-hidden="true" /></>
                    ) : (
                      <span className="k-think-dot" data-testid="klair-thinking-dot" />
                    )}
                  </div>
                </div>
              )}
              {/* KLA-053: the scroll anchor. Its margin keeps the last
                  bubble clear of the fixed composer bar. */}
              <div ref={endRef} aria-hidden="true" style={{ scrollMarginBottom: "12rem" }} />
            </div>
          </>
        )}

        {err && (<div className="mt-4 text-sm" style={{ color: "hsl(var(--destructive))" }} data-testid="conv-error">{err}</div>)}
      </div>

      {/* Composer, only for conversation phases (the wizard has its own controls). */}
      {!isItem && (
        <div className="fixed bottom-0 inset-x-0 border-t" style={{ borderColor: "hsl(var(--klareet-line-soft))", background: "hsl(var(--background) / 0.94)", backdropFilter: "blur(8px)", paddingBottom: "env(safe-area-inset-bottom)" }}>
          <div className="max-w-3xl mx-auto px-4 py-4">
            {isGate && (
              <>
                <p className="k-quiet mb-2">ready when you are</p>
                <div className="flex flex-wrap gap-2" data-testid="gate-palette">
                  {(current?.quick_replies || []).map((opt, i) => {
                    const cardKey = `${current?.stage}|${current?.turn_index}`;
                    const used = usedCardKey === cardKey;
                    return (
                      <button key={i} onClick={() => { setUsedCardKey(cardKey); submit(null, opt, false); }} disabled={busy || used} className={i === 0 ? "k-btn k-btn-primary" : "k-btn k-btn-ghost"} data-testid={`gate-choice-${i + 1}`}>{opt}</button>
                    );
                  })}
                </div>
              </>
            )}
            {isHostile && (
              <>
                <p className="k-quiet mb-2">your call, no wrong move here</p>
                <div className="flex flex-wrap gap-2" data-testid="hostile-palette">
                  {(current?.quick_replies || []).map((opt, i) => {
                    const cardKey = `${current?.stage}|${current?.turn_index}`;
                    const used = usedCardKey === cardKey;
                    return (
                      <button key={i} onClick={() => { setUsedCardKey(cardKey); submit(null, opt, false); }} disabled={busy || used} className="k-pill" data-testid={`hostile-choice-${i + 1}`}>{opt}</button>
                    );
                  })}
                </div>
              </>
            )}
            {isResume && (
              <div className="k-break-card mb-4" data-testid="break-card">
                <span className="k-coffee" aria-hidden="true">
                  <Coffee size={26} />
                  <i className="k-steam k-steam-1" />
                  <i className="k-steam k-steam-2" />
                  <i className="k-steam k-steam-3" />
                </span>
                <p className="k-quiet !text-sm" data-testid="break-fact">{BREAK_FACTS[breakIdx % BREAK_FACTS.length]}</p>
              </div>
            )}
            {isResume && (
              <div className="flex justify-center" data-testid="resume-palette">
                {(current?.quick_replies || ["I'm ready now"]).map((opt, i) => {
                  const cardKey = `${current?.stage}|${current?.turn_index}`;
                  const used = usedCardKey === cardKey;
                  return (
                    <button key={i} onClick={() => { setUsedCardKey(cardKey); submit(null, opt, false); }} disabled={busy || used} className="k-btn k-btn-primary" data-testid={`resume-choice-${i + 1}`}>{opt}</button>
                  );
                })}
              </div>
            )}
            {isCareChoice && (
              <>
                <p className="k-quiet mb-2">your call, and there is no wrong answer here</p>
                <div className="flex flex-col gap-2" data-testid="care-choice-palette">
                  {(current?.quick_replies || []).map((opt, i) => {
                    const cardKey = `${current?.stage}|${current?.turn_index}`;
                    const used = usedCardKey === cardKey;
                    return (
                      <button key={i} onClick={() => { setUsedCardKey(cardKey); submit(null, opt, false); }} disabled={busy || used} className="k-pill k-pill-block text-left" data-testid={`care-choice-${i + 1}`}>{opt}</button>
                    );
                  })}
                </div>
              </>
            )}
            {isChatText && (
              <div>
                {/* KLA-018 + KLA-021: starters wait behind "need ideas?"
                    and self-open after 15 quiet seconds; the question
                    always lands first. Typing stays first-class. */}
                {revealed && Array.isArray(current?.suggested_replies) && current.suggested_replies.length > 0 && !ideasOpen && (
                  <button type="button" onClick={() => setIdeasOpen(true)} className="k-suggest mb-2" data-testid="chat-need-ideas">
                    <Sparkles size={13} aria-hidden="true" /> need ideas?
                  </button>
                )}
                {revealed && Array.isArray(current?.suggested_replies) && current.suggested_replies.length > 0 && ideasOpen && (
                  <div className="mb-2" data-testid="chat-suggested">
                    <div className="flex flex-wrap gap-2">
                      {current.suggested_replies.map((s, i) => (
                        <button key={i} type="button" onClick={() => setText(s)} disabled={busy} className="k-suggest k-pop-in" style={{ animationDelay: `${i * 55}ms` }} data-testid={`chat-suggested-${i + 1}`}><Sparkles size={13} aria-hidden="true" /> {s}</button>
                      ))}
                    </div>
                    <p className="k-quiet !text-xs mt-1.5">tap one to start, or say it your own way</p>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <textarea
                    data-testid="chat-input"
                    aria-label="Your message to Klair"
                    className="k-input resize-none klareet-serif"
                    rows={2}
                    placeholder="say it how you'd say it"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey && !isCoarsePointer()) { e.preventDefault(); if (text.trim()) submit(null, text.trim(), false); } }}
                  />
                  <button onClick={() => text.trim() && submit(null, text.trim(), false)} disabled={busy || !text.trim()} className="k-btn k-btn-primary" data-testid="chat-send" aria-label="send"><ArrowRight size={16} /></button>
                </div>
              </div>
            )}
            {isContext && (
              <div data-testid="context-composer">
                <div className="flex flex-wrap gap-2 mb-3" data-testid="context-pills">
                  {(current?.quick_replies || []).map((opt, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCtxPick(opt)}
                      disabled={busy}
                      data-testid={`context-pill-${i + 1}`}
                      className="px-4 py-2 rounded-full text-sm border transition-all duration-200 hover:-translate-y-0.5"
                      style={{
                        background: ctxPick === opt ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-canvas-2))",
                        color: ctxPick === opt ? "#fff" : "hsl(var(--klareet-ink))",
                        borderColor: ctxPick === opt ? "hsl(var(--klareet-ink))" : "hsl(var(--klareet-line-soft))",
                      }}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
                <div className="flex items-end gap-2">
                  <textarea
                    data-testid="context-note-input"
                    aria-label="A note about your course, college, or work, optional"
                    className="k-input resize-none"
                    rows={2}
                    placeholder="optional: your course, college, or work"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                  />
                  <button
                    onClick={() => {
                      const note = text.trim();
                      const combined = note ? `${ctxPick}. ${note}` : ctxPick;
                      setCtxPick("");
                      submit(null, combined, false);
                    }}
                    disabled={busy || !ctxPick}
                    className="k-btn k-btn-primary"
                    data-testid="context-continue"
                    aria-label="continue"
                  >
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>
            )}
            {!isGate && !isHostile && !isResume && !isCareChoice && !isChatText && !isContext && (
              <div className="k-quiet">{busy ? (streaming && streamText ? "Klair is talking." : "Klair is thinking.") : "Ready."}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
